import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AtSign, ArrowRight, Loader2, Wallet, Check } from "lucide-react";
import { claimUsername, lookupUsername } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const USERNAME_RE = /^[a-z0-9_]{3,30}$/;

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [value, setValue] = useState("");
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null); // null | true | false
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // If the user has finished onboarding (username set), bounce to /welcome
  // (tier-aware — sends architects to /console, free operators to /truths).
  useEffect(() => {
    if (!loading && user?.username_set) {
      navigate("/welcome", { replace: true });
    }
  }, [loading, user, navigate]);

  // Debounced username-availability check.
  useEffect(() => {
    setAvailable(null);
    setError(null);
    const normalized = value.trim().toLowerCase().replace(/^@/, "");
    if (!normalized) return;
    if (!USERNAME_RE.test(normalized)) {
      setError("3–30 chars · lowercase letters, numbers, underscore only.");
      return;
    }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await lookupUsername(normalized).catch((e) => {
          if (e?.response?.status === 404) return { __free: true };
          throw e;
        });
        setAvailable(Boolean(res.__free));
        if (!res.__free) setError(`@${normalized} is already taken.`);
      } catch (e) {
        setError("Could not check availability. Try again.");
      } finally {
        setChecking(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  const submit = async (e) => {
    e?.preventDefault();
    const normalized = value.trim().toLowerCase().replace(/^@/, "");
    if (!USERNAME_RE.test(normalized)) {
      setError("3–30 chars · lowercase letters, numbers, underscore only.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await claimUsername(normalized);
      toast.success(`@${normalized} is yours.`);
      await refresh?.();
      navigate("/welcome", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Could not claim that handle. Try a different one."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center font-mono text-xs">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const normalized = value.trim().toLowerCase().replace(/^@/, "");
  const okShape = USERNAME_RE.test(normalized);
  const canSubmit = okShape && available === true && !submitting;

  return (
    <div className="min-h-screen bg-black text-zinc-100 relative">
      <div className="absolute inset-0 scanline pointer-events-none" />
      <div className="relative z-10 max-w-xl mx-auto px-6 py-16">
        <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400 mb-3 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-sentinel-pulse" />
          One last step
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl leading-tight">
          Claim your wallet handle.
        </h1>
        <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
          Pick a public username — this is how other people send AEGIS to your
          wallet. Lowercase letters, numbers, and underscores only. You can
          change it later in settings, but the original handle never goes away.
        </p>

        <div className="mt-8 border border-zinc-900 bg-zinc-950 p-5 text-xs text-zinc-400 leading-relaxed flex gap-3">
          <Wallet className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
          <div>
            Signed in as <span className="text-zinc-200">{user.email}</span>.
            Once you claim a handle, anyone can transfer AEGIS to you via
            <span className="text-amber-300 font-mono"> @yourhandle</span> from
            the Wallet page.
          </div>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
              Pick a handle
            </div>
            <div className="relative">
              <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="yourhandle"
                data-testid="onboarding-username-input"
                className="w-full bg-black border border-zinc-800 focus:border-amber-500/60 outline-none text-zinc-100 pl-9 pr-28 py-3 font-mono text-sm"
                maxLength={30}
                spellCheck={false}
                autoComplete="off"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.25em]">
                {checking && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
                {!checking && available === true && (
                  <span className="text-emerald-400 flex items-center gap-1" data-testid="onboarding-username-available">
                    <Check className="h-3 w-3" /> available
                  </span>
                )}
                {!checking && available === false && (
                  <span className="text-rose-400" data-testid="onboarding-username-taken">taken</span>
                )}
              </div>
            </div>
            {error && (
              <div
                className="mt-2 text-[11px] text-rose-300"
                data-testid="onboarding-username-error"
              >
                {error}
              </div>
            )}
            {!error && okShape && available === true && (
              <div className="mt-2 text-[11px] text-emerald-400">
                Looks good — people will send AEGIS to{" "}
                <span className="font-mono text-amber-300">@{normalized}</span>.
              </div>
            )}
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="onboarding-submit-btn"
            className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-zinc-950 transition-colors text-xs uppercase tracking-[0.35em] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-emerald-400"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Claim &amp; enter the Sentinel
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          · Your handle is public · Your email is not ·
        </p>
      </div>
    </div>
  );
}
